import * as RdxPopover from '@radix-ui/react-popover';
import {
  CheckIcon,
  ChevronDownIcon,
  Cross2Icon,
  Pencil1Icon,
  PlusIcon,
} from '@radix-ui/react-icons';
import { FormProvider } from 'react-hook-form';
import cn from '../../../app/utils/cn';
import { Button } from '../Button';
import ContractAddressFields from '../ContractAddressFields';
import { IconButton } from '../IconButton';
import { Input } from '../Input';
import { TextButton } from '../TextButton';
import { useLocationComboboxController } from './useLocationComboboxController';

interface ILocationComboboxProps {
  value?: string | null;
  onChange: (locationId: string | null, locationName?: string) => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
}

export default function LocationCombobox({
  value,
  onChange,
  disabled,
  placeholder,
  className,
}: ILocationComboboxProps) {
  const {
    t,
    form,
    open,
    search,
    showForm,
    isEditing,
    isFormBusy,
    isAddressVisible,
    isDeleteConfirming,
    isLocationsLoading,
    selectedLocation,
    filteredLocations,
    exactMatch,
    setSearch,
    handleOpenChange,
    handleSelect,
    handleClear,
    openCreateForm,
    openEditForm,
    toggleAddressVisibility,
    toggleDeleteConfirm,
    handleSubmit,
    resetForm,
  } = useLocationComboboxController({ value, onChange });

  return (
    <div className={cn('w-full', className)}>
      <RdxPopover.Root open={open} onOpenChange={handleOpenChange}>
        <div className="relative flex w-full items-center">
          <RdxPopover.Trigger asChild>
            <button
              type="button"
              disabled={disabled}
              aria-expanded={open}
              aria-haspopup="listbox"
              aria-label={t('location.combobox.triggerAriaLabel')}
              className={cn(
                'bg-surface-input border-border-input text-text-primary flex h-10 w-full items-center justify-between gap-2 rounded border-2 border-solid px-2 py-1.5 text-sm shadow-md outline-none',
                'focus:border-green-600 dark:focus:border-green-400',
                'disabled:cursor-not-allowed disabled:opacity-50',
                selectedLocation && 'pr-10',
              )}
            >
              <span className="min-w-0 flex-1 truncate text-left">
                {selectedLocation ? (
                  selectedLocation.name
                ) : (
                  <span className="text-text-tertiary">
                    {placeholder ?? t('location.combobox.placeholder')}
                  </span>
                )}
              </span>
              <ChevronDownIcon className="text-text-tertiary shrink-0" />
            </button>
          </RdxPopover.Trigger>

          {selectedLocation && !disabled && (
            <IconButton
              size="md"
              onClick={handleClear}
              aria-label={t('location.combobox.clear')}
              className="absolute right-7"
            >
              <Cross2Icon className="h-4 w-4" />
            </IconButton>
          )}
        </div>

        <RdxPopover.Portal>
          <RdxPopover.Content
            className="bg-surface-card border-border-default z-50 mt-1 max-h-[70vh] w-[var(--radix-popover-trigger-width)] min-w-[260px] overflow-y-auto rounded-lg border p-2 shadow-lg"
            sideOffset={4}
            align="start"
          >
            {!showForm && (
              <>
                <div className="mb-2">
                  <Input
                    name="location-search"
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={t('location.combobox.searchPlaceholder')}
                    aria-label={t('location.combobox.searchPlaceholder')}
                  />
                </div>

                <ul
                  role="listbox"
                  className="flex max-h-60 flex-col gap-0.5 overflow-y-auto"
                >
                  {isLocationsLoading && (
                    <li className="text-text-tertiary p-2 text-xs">
                      {t('location.combobox.loading')}
                    </li>
                  )}

                  {!isLocationsLoading && filteredLocations.length === 0 && (
                    <li className="text-text-tertiary p-2 text-xs">
                      {t('location.combobox.empty')}
                    </li>
                  )}

                  {filteredLocations.map((location) => {
                    const checked = location.id === value;
                    return (
                      <li
                        key={location.id}
                        role="option"
                        aria-selected={checked}
                        className="hover:bg-hover-subtle dark:hover:bg-hover-medium flex items-center gap-1 rounded"
                      >
                        <button
                          type="button"
                          onClick={() => handleSelect(location)}
                          className="text-text-primary flex min-h-10 flex-1 items-center justify-between gap-2 px-2 py-2 text-left text-sm"
                        >
                          <span className="truncate">{location.name}</span>
                          {checked && (
                            <CheckIcon className="text-text-primary shrink-0" />
                          )}
                        </button>
                        <IconButton
                          size="md"
                          onClick={() => openEditForm(location)}
                          aria-label={t('location.combobox.editAriaLabel', {
                            name: location.name,
                          })}
                          className="shrink-0"
                        >
                          <Pencil1Icon className="h-3.5 w-3.5" />
                        </IconButton>
                      </li>
                    );
                  })}
                </ul>

                <div className="border-border-default mt-2 flex flex-col border-t pt-2">
                  <TextButton
                    onClick={openCreateForm}
                    disabled={!search.trim() && exactMatch}
                    className="text-text-primary hover:bg-hover-subtle dark:hover:bg-hover-medium min-h-10 w-full justify-start gap-2 px-2 py-2"
                  >
                    <PlusIcon className="shrink-0" />
                    <span className="truncate">
                      {search.trim()
                        ? t('location.combobox.createNamed', {
                            name: search.trim(),
                          })
                        : t('location.combobox.createNew')}
                    </span>
                  </TextButton>
                </div>
              </>
            )}

            {showForm && (
              <FormProvider {...form}>
                <div className="flex flex-col gap-3">
                  <h3 className="text-text-primary text-sm font-semibold">
                    {isEditing
                      ? t('location.combobox.editTitle')
                      : t('location.combobox.createTitle')}
                  </h3>

                  <Input
                    type="text"
                    placeholder={t('location.combobox.namePlaceholder')}
                    aria-label={t('location.combobox.namePlaceholder')}
                    maxLength={100}
                    disabled={isFormBusy}
                    {...form.register('name')}
                  />

                  <TextButton
                    onClick={toggleAddressVisibility}
                    className="text-text-secondary min-h-10 w-full justify-start"
                  >
                    {t('location.combobox.addressToggle')}
                  </TextButton>

                  {isAddressVisible && (
                    <ContractAddressFields disabled={isFormBusy} />
                  )}

                  <div className="flex flex-col gap-2 sm:flex-row">
                    <Button
                      color="ghost"
                      labelPath="location.combobox.cancel"
                      onClick={resetForm}
                      disabled={isFormBusy}
                    />
                    <Button
                      color="primary"
                      labelPath={
                        isEditing
                          ? 'location.combobox.save'
                          : 'location.combobox.confirm'
                      }
                      onClick={handleSubmit}
                      disabled={isFormBusy}
                      isLoading={isFormBusy}
                    />
                  </div>

                  {isEditing && (
                    <Button
                      color="danger"
                      labelPath={
                        isDeleteConfirming
                          ? 'location.combobox.confirmDelete'
                          : 'location.combobox.delete'
                      }
                      onClick={toggleDeleteConfirm}
                      disabled={isFormBusy}
                    />
                  )}
                </div>
              </FormProvider>
            )}
          </RdxPopover.Content>
        </RdxPopover.Portal>
      </RdxPopover.Root>
    </div>
  );
}
